import { useState, useEffect, useCallback } from 'react';
import useForm from '@base/Form/useForm';
import { useSnackbar } from '@stores/snackbar';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import segmentStorage from '@events/segmentStorage';
import { segment } from '@actions/analytics';
import fetch from '@api/fetchers';
import useCountries from '@api/hooks/useCountries';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import Me from '@models/Me';
import { Fonts, Spacings } from '@styles/index';
import { getIsLoggedIn, getMePhone, getFullName } from '@api/selectors/auth';
import Button from '@base/Button/Button';
import {
  getSetLocalPhoneNumber,
  getSetLocalEmailAndName,
  getIsRegistered
} from '@stores/selectors/login';
import { getMeLatestUserSubscriptions } from '@api/selectors/subscription';
import useLoginStore from '@stores/login/loginStore';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import styled from '@emotion/styled';
import { getSubscriptionType } from '@Analytics/Segment';
import {
  getEnquiryFormEndpoint,
  getGoalSpecificCityCentreEndpoint
} from '@cont/OfflineCentres/apiHelper';
import {
  DEFAULT_COUNTRY,
  FORM_ENQUIRY_FORM_CONTINUE,
  FORM_ENQUIRY_FORM_SUBMITTED,
  FORM_ENQUIRY_FORM_VIEWED,
  goalDetailsData
} from '@cont/OfflineCentres/constants';
import P2 from '@base/P2/P2';
import DialogEnquiryGoalDetails from './DialogEnquiryGoalDetails';
import DialogEnquiryPersonalDetails from './DialogEnquiryPersonalDetails';

const formState = {
  name: '',
  phone: '',
  email: '',
  goal: '',
  centre: ''
};

const Wrapper = styled.div`
  width: 100%;
  min-height: 100%;
`;

const LoaderWrapper = styled.div`
  width: 100%;
  min-height: 462px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FormContainer = styled.div`
  width: 100%;
`;

const SubmitEnquiryButon = styled(Button)`
  width: 328px;
  margin-top: ${Spacings.SPACING_4B};
`;

const AcceptTerms = styled.div`
  margin-top: ${Spacings.SPACING_3B};
  font-size: ${Fonts.P2};
`;

const TermsContent = styled.p`
  font-size: ${Fonts.EXTRA_SMALL};
  line-height: 15px;
  font-weight: ${Fonts.SEMIBOLD};
`;

const TermsLink = styled.a`
  text-decoration: none;
  color: var(--color-i-black);
  cursor: pointer;
`;

const StyP2 = styled(P2)`
  display: inline-block;
`;

const TermsText = styled(StyP2)`
  font-weight: ${Fonts.SEMIBOLD};
`;

const DialogEnquiryForm = ({
  cmsData,
  offlineGoals,
  showThankyouPage
  // formType,
}) => {
  const setLocalPhoneNumber = useLoginStore(getSetLocalPhoneNumber);
  const setLocalEmailAndName = useLoginStore(getSetLocalEmailAndName);
  const { open: showSnackbar } = useSnackbar();

  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { isValidating } = useMyInfo();
  const plusSubscriptions = useMyInfo(getMeLatestUserSubscriptions);
  const activeSubscription =
    Me.getActiveSubscriptionToAnyGoal(plusSubscriptions) ?? {};

  const { timeRemaining = 0 } = activeSubscription;
  const default_is_subscription_active = timeRemaining > 0;
  let subscription = {};
  if (default_is_subscription_active) {
    subscription = activeSubscription.subscription;
  }
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const [payload, setPayload] = useState(null);
  const countries = useCountries();
  const defaultPhoneNumber = useMyInfo(getMePhone);
  const defaultName = useMyInfo(getFullName);
  const isRegistered = useLoginStore(getIsRegistered);

  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    goalDetailsData.Goal.options = offlineGoals;
    goalDetailsData.Centre.options = [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [defaultFormState, setDefaultFormState] = useState(formState);
  const [goalDetails, setGoalDetails] = useState(goalDetailsData);

  const { registrar, isSubmitting, onFormSubmit } = useForm({
    data: defaultFormState
  });

  useEffect(() => {
    setShowLoader(isValidating);
  }, [isValidating]);

  useEffect(() => {
    const formStateWithNumber = {
      ...defaultFormState,
      phone: defaultPhoneNumber,
      name: defaultName
    };
    setDefaultFormState(formStateWithNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPhoneNumber, defaultName]);

  const getCentresDataOfAGoal = async (goalUID) => {
    const cityCentreList = await fetch(
      getGoalSpecificCityCentreEndpoint(goalUID.toUpperCase())
    );
    const cityCentreData = [];
    cityCentreList?.forEach((city) => {
      city.centres.forEach((centre, index) => {
        cityCentreData.push({
          id: `${city.id}${index}`,
          label: `${city.name} - ${centre.name}`,
          value: centre.uid
        });
      });
    });
    return cityCentreData;
  };

  useEffect(() => {
    const selectedGoalUID = registrar.data.Goal?.value?.toLowerCase();
    if (selectedGoalUID) {
      const goalDetailsInfo = { ...goalDetailsData };
      getCentresDataOfAGoal(selectedGoalUID).then((centreOptions) => {
        goalDetailsInfo.Centre.options = centreOptions;
        goalDetailsInfo.Centre.isHidden = !centreOptions.length;
        setGoalDetails(goalDetailsInfo);
      });

      registrar.setData('Centre', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmsData?.enquiryForm?.class, registrar.data?.Goal?.value]);

  const [country, setCountry] = useState(
    countries.length
      ? countries.find((c) => c.code === DEFAULT_COUNTRY.code)
      : DEFAULT_COUNTRY
  );

  useEffect(() => {
    segmentStorage.setItem('lps', 'Enquiry Form');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isUserDataLoaded = useMyInfo(getIsLoggedIn);
  const { isLoading: isUserDataLoading, isValidating: userDataNeedsLoading } =
    useMyInfo();

  const isUserLoggedIn = !isUserDataLoading && isUserDataLoaded;
  const isUserLoggedOut = !userDataNeedsLoading && !isUserDataLoaded;

  useEffect(() => {
    if (isUserLoggedIn || isUserLoggedOut)
      segment.track(FORM_ENQUIRY_FORM_VIEWED, {
        // form_type: formType || '',
        user_subscription_plan: getSubscriptionType(subscription?.type)
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoggedIn, isUserLoggedIn, subscription.type]);

  const changeCountry = (countryOption) => {
    setCountry(countries.find((c) => c.code === countryOption.value));
  };

  const submitEnquiryData = useCallback(() => {
    fetch(getEnquiryFormEndpoint(), {
      requestType: 'POST',
      body: { data: payload }
    })
      .then(() => {
        showThankyouPage(true);
        segment.track(FORM_ENQUIRY_FORM_SUBMITTED, {
          class: payload.class,
          goal_uid: payload.goal,
          goal_name: payload.goal_name,
          centre: payload.centre_name || '',
          learner_name: payload.name,
          // form_type: formType,
          user_subscription_plan: getSubscriptionType(subscription?.type)
        });
      })
      .catch(() => {
        showSnackbar({
          message: 'Something went wrong, Try again later',
          type: 'error'
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  useEffect(() => {
    if (isLoggedIn && payload) {
      submitEnquiryData();
    }
  }, [isLoggedIn, payload, submitEnquiryData]);

  useEffect(() => {
    if (isRegistered) {
      setLocalEmailAndName('', payload?.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRegistered]);

  const onSubmit = (data) => {
    const goalDetailsKeys = Object.keys(goalDetails ?? {});
    const goalDetailData = {};
    goalDetailsKeys.forEach((goalDetailsKey) => {
      goalDetailData[goalDetailsKey.toLowerCase()] =
        data[goalDetailsKey]?.value;
    });

    setPayload({
      name: data.name,
      phone: data.phone,
      email: data.email,
      ...goalDetailData,
      goal_name: data.Goal.label,
      centre_name: data.Centre?.label || ''
    });

    if (!isLoggedIn) {
      setLocalPhoneNumber(String(data.phone), DEFAULT_COUNTRY);
      setLocalEmailAndName('', data?.name);
      openLoginDrawer();
    }

    segment.track(FORM_ENQUIRY_FORM_CONTINUE, {
      class: goalDetailData.class,
      goal_uid: goalDetailData.goal,
      goal_name: data.Goal.label,
      centre: data.Centre?.label || '',
      learner_name: data.name,
      // form_type: formType,
      user_subscription_plan: getSubscriptionType(subscription?.type)
    });
  };

  const handleOnSubmit = onFormSubmit(onSubmit);

  if (showLoader) {
    return (
      <Wrapper>
        <LoaderWrapper>
          <CircularLoader />
        </LoaderWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <FormContainer>
        <DialogEnquiryPersonalDetails
          registrar={registrar}
          changeCountry={changeCountry}
          country={country}
          countries={countries}
        />
        <DialogEnquiryGoalDetails registrar={registrar} fields={goalDetails} />
      </FormContainer>
      <SubmitEnquiryButon
        onClick={handleOnSubmit}
        width="328px"
        variant={isSubmitting ? 'disabled' : 'filled'}
        label="Continue"
        color="black"
      />
      <AcceptTerms>
        <TermsContent>
          <StyP2>By continuing, you agree to</StyP2>
          <TermsLink target="_blank" href="/terms">
            <TermsText>&nbsp;Unacademy’s terms</TermsText>
          </TermsLink>
        </TermsContent>
      </AcceptTerms>
    </Wrapper>
  );
};

export default DialogEnquiryForm;
